// ── guide figures ───────────────────────────────────────────────────────────
// Designed pictures of innernet, drawn inline where a guide (or a docs page)
// writes `:::figure <name> <caption>`. They are not screenshots: each is the
// real screen reduced to what one step is about, in the real words the app
// uses, with the one control to press marked and numbered (.gf-hot / .gf-n).
//
// Every label here is copied from the component that renders it (the guide
// inventory of 2026-09-29). When a label in the app changes, change it here in
// the same pass, or the guide starts telling people to press a word that no
// longer exists.
//
// No hooks, no client state: the figures render on the server inside the docs
// and inside the guides' client reader alike.

import React from 'react'
import BrandIcon from '../BrandIcon'
import './guide-figures.css'

// ── primitives ────────────────────────────────────────────────────────────

type StageKind = 'app' | 'term' | 'chat' | 'plain' | 'dark'

function Stage({ kind = 'app', title, url, children }: { kind?: StageKind; title?: string; url?: string; children: React.ReactNode }) {
  const cls = kind === 'term' ? 'gf__stage gf__stage--term' : kind === 'dark' ? 'gf__stage gf__stage--term gf__stage--dark' : 'gf__stage'
  return (
    <div className={cls}>
      {(title || url || kind === 'app' || kind === 'term' || kind === 'chat') && (
        <div className="gf__bar">
          <span className="gf__dots" aria-hidden><i /><i /><i /></span>
          {url ? <span className="gf__url">{url}</span> : title ? <span>{title}</span> : null}
        </div>
      )}
      {children}
    </div>
  )
}

/** the mark on the thing to press, with its number */
function Hot({ n, box, children }: { n?: number; box?: boolean; children: React.ReactNode }) {
  return (
    <span className={box ? 'gf-hot gf-hot--box' : 'gf-hot'}>
      {children}
      {n != null && <span className="gf-n" aria-hidden>{n}</span>}
    </span>
  )
}

function Legend({ items }: { items: string[] }) {
  return (
    <ol className="gf-legend">
      {items.map((t, i) => (
        <li key={i}><span className="gf-n" aria-hidden>{i + 1}</span><span>{t}</span></li>
      ))}
    </ol>
  )
}

function Chip({ on, lift, ghost, add, children }: { on?: boolean; lift?: boolean; ghost?: boolean; add?: boolean; children: React.ReactNode }) {
  return (
    <span className={add ? 'gf-chip gf-chip--add' : 'gf-chip'} data-on={on || undefined} data-lift={lift || undefined} data-ghost={ghost || undefined}>
      {children}
    </span>
  )
}

function Row({ name, meta, sub, dot, dim }: { name: React.ReactNode; meta?: React.ReactNode; sub?: React.ReactNode; dot?: 'live' | 'quiet'; dim?: boolean }) {
  return (
    <div className="gf-row" data-dim={dim || undefined}>
      <span className="gf-row__name" style={sub ? { whiteSpace: 'normal' } : undefined}>
        {dot && <span className="gf-dot" data-live={dot === 'live' || undefined} aria-hidden />}
        {name}
        {sub && <span className="gf-row__sub">{sub}</span>}
      </span>
      {meta != null && <span className="gf-row__meta">{meta}</span>}
    </div>
  )
}

function Crumb({ trail, menu = 1 }: { trail: string[]; menu?: number | null }) {
  return (
    <span className="gf-crumb">
      <b>innernet</b>
      {menu != null ? <Hot n={menu}><span className="gf-crumb__menu">≡</span></Hot> : <span className="gf-crumb__menu">≡</span>}
      {trail.map((t, i) => (
        <React.Fragment key={i}>
          <span>/</span>
          {i === trail.length - 1 ? <b>{t}</b> : <span>{t}</span>}
        </React.Fragment>
      ))}
      <span>/</span>
      <span>search</span>
    </span>
  )
}

function Ticker({ words, on }: { words: string[]; on: string }) {
  const at = words.indexOf(on)
  const ordered = at > 0 ? [...words.slice(at), ...words.slice(0, at)] : words
  return (
    <div className="gf-ticker" aria-hidden>
      {ordered.map((w) => <span key={w} data-on={w === on || undefined}>{w}</span>)}
    </div>
  )
}

const SPACES = ['projects', 'you', 'netti', 'skills', 'connections', 'activity', 'tasks', 'settings']

const Chevron = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M4.5 2.5 8 6l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

function Pill({ children, mark }: { children: React.ReactNode; mark?: boolean }) {
  return (
    <span className="gf-pill">
      {children}
      <i>{mark ? <BrandIcon name="innernet" size={14} /> : <Chevron />}</i>
    </span>
  )
}

function Netti({ size = 64 }: { size?: number }) {
  return <span className="gf-netti" style={{ ['--gf-n' as string]: `${size}px` } as React.CSSProperties} aria-hidden />
}

function You({ name = 'sam' }: { name?: string }) {
  return (
    <span className="gf-you">{name}<span className="gf-avatar" aria-hidden>{name[0]}</span></span>
  )
}

function Term({ lines }: { lines: [string, string?][] }) {
  return (
    <div>
      {lines.map(([t, kind], i) => (
        <div key={i} className={`gf-term__l${kind ? ` gf-term__${kind}` : ''}`}>{t || ' '}</div>
      ))}
    </div>
  )
}

function Call({ tool }: { tool: string }) {
  return (
    <span className="gf-call"><BrandIcon name="innernet" size={14} />{tool}</span>
  )
}

// ── the figures ───────────────────────────────────────────────────────────

const FIGURES: Record<string, () => React.ReactNode> = {
  // the whole loop, as three rooms
  loop: () => (
    <Stage kind="plain">
      <div className="gf-split" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
        <div>
          <p className="gf-col-label">1 · you say it, anywhere</p>
          <div className="gf-chat">
            <span className="gf-say">let&apos;s go usage-based on pricing</span>
            <Call tool="innernet_capture" />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <p className="gf-col-label">2 · netti folds it in</p>
          <Netti size={58} />
          <span className="gf-row__meta">pricing · updated · 1 line of history</span>
        </div>
        <div>
          <p className="gf-col-label">3 · any tool, any day</p>
          <div className="gf-chat">
            <span className="gf-say">what did we decide on pricing?</span>
            <Call tool="innernet_context" />
            <span className="gf-reply">usage-based, since the 12th. before that it was a flat seat.</span>
          </div>
        </div>
      </div>
    </Stage>
  ),

  signup: () => (
    <Stage url="innernet.live/signup">
      <div style={{ maxWidth: '22rem', margin: '0.4rem auto 0.3rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <p style={{ margin: 0, fontSize: '1.2rem', letterSpacing: '-0.02em', lineHeight: 1.2 }}>one memory of you,<br />carried across every ai.</p>
        <Hot n={1} box><span className="gf-field" style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--gf-ink)' }}><BrandIcon name="Google" size={14} />begin with Google</span></Hot>
        <span className="gf-field" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--gf-ink)' }}><BrandIcon name="GitHub" size={14} />begin with Github</span>
        <span className="gf-row__meta" style={{ textAlign: 'center' }}>or with your email</span>
        <span className="gf-field">your email</span>
        <span style={{ alignSelf: 'flex-end' }}><Hot n={2}><Pill mark>begin</Pill></Hot></span>
      </div>
    </Stage>
  ),

  'netti-birth': () => (
    <Stage kind="dark">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.9rem', padding: '1.2rem 0 0.6rem', fontFamily: 'var(--font-sans, inherit)' }}>
        <span className="gf-term__dim">this is a cell.</span>
        <Hot n={1}><Netti size={84} /></Hot>
        <span>hold the cell to create your netti</span>
      </div>
    </Stage>
  ),

  'netti-born': () => (
    <Stage kind="plain">
      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Netti size={96} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', minWidth: 0 }}>
          <span style={{ fontSize: '1.15rem' }}>this is <Hot n={1}><span className="gf-link">sam</span></Hot>&apos;s netti</span>
          <span style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <span className="gf-link">put your name on it</span>
            <Hot n={2}><span className="gf-link">create another</span></Hot>
          </span>
          <span><Hot n={3}><Pill mark>sign up to save your netti</Pill></Hot></span>
        </div>
      </div>
    </Stage>
  ),

  arrival: () => (
    <Stage url="innernet.live">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.8rem', padding: '0.4rem 0' }}>
        <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'center' }}>
          <Netti size={52} />
          <span style={{ fontSize: '1.15rem' }}>your netti is here, sam.</span>
        </div>
        <span style={{ display: 'flex', gap: '1.1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Hot n={1}><Pill mark>begin</Pill></Hot>
          <span className="gf-link">enter innernet</span>
        </span>
      </div>
    </Stage>
  ),

  // the CLI's own words, line for line (apps/cli/src/commands/connect.ts), for a
  // stand-in person in a repo called lisbon-app, on the first run: the plan,
  // then each step. The end of the run is 'terminal-whatnow', below.
  'terminal-connect': () => (
    <Stage kind="term" title="lisbon-app — zsh">
      <Term
        lines={[
          ['~/code/lisbon-app $ npx innernet', 'p'],
          [''],
          ['  innernet  one memory for every ai tool you use', ''],
          ['  ────────────────────────────────────────────────────', 'dim'],
          ['  three things happen here, and each one says what it did:', 'dim'],
          ['    1  you sign in, once, in your browser', ''],
          ['    2  every ai tool on this machine is pointed at your memory', ''],
          ['    3  this repo gets a memory of its own', ''],
          [''],
          ['  1 · sign in', ''],
          ['  · your browser is opening innernet.live. sign in there, or make an account.', ''],
          ['  ✓ signed in as sam@example.com', 'ok'],
          [''],
          ['  2 · your ai tools  → https://innernet.live/api/mcp', ''],
          ['  ✓ Claude Code        claude mcp (user scope)', 'ok'],
          ['  ✓ Cursor             ~/.cursor/mcp.json', 'ok'],
          ['  ✓ Codex CLI          ~/.codex/config.toml', 'ok'],
          ['  · each got one entry in its own settings, pointed at your memory.', 'dim'],
          [''],
          ['  in the browser, add the link once (about two minutes each):', 'dim'],
          ['  · Claude             Customize → Connectors → Add, paste the link', ''],
          ['  · ChatGPT            developer mode on, chatgpt.com/plugins → +, paste the link', ''],
          [''],
          ['  3 · this folder  ~/code/lisbon-app', ''],
          ['  · netti can start a memory for this repo. it reads the readme and the file', 'dim'],
          ['    names, nothing else, and designs the pages the memory will keep.', 'dim'],
          ['  start a memory for lisbon-app? [Y/n] y', ''],
          ['  ✓ memory lisbon-app started, with 6 pages netti keeps current:', 'ok'],
          ['      vision · technical · pricing · bookings · people · open questions', 'dim'],
          ['  ✓ every commit and pull here is remembered, with its message (.innernet/hooks)', 'ok'],
          ['  ✓ AGENTS.md now tells coding agents where this memory is (CLAUDE.md imports it)', 'ok'],
          ['  · commit .innernet/ AGENTS.md CLAUDE.md so a teammate who clones reads the same memory', 'dim'],
        ]}
      />
    </Stage>
  ),

  // the end of the same first run: what to restart, what to say, where next
  'terminal-whatnow': () => (
    <Stage kind="term" title="lisbon-app — zsh">
      <Term
        lines={[
          ['  ────────────────────────────────────────────────────', 'dim'],
          ["  you're connected. every tool above now reads and writes one memory,", ''],
          ['  so what you tell one of them, the others already know.', ''],
          [''],
          ['  what now', ''],
          ['  1  if Claude Code, Cursor and Codex CLI are open, restart them.', ''],
          ['     tools read their settings when they start.', 'dim'],
          ['  2  open a new chat in any of them and talk the way you always do. try:', ''],
          [''],
          ['       "load my projects from innernet"', ''],
          ['         checks the connection. it lists your memories, or says there are none yet.', 'dim'],
          ['       "what\'s in the lisbon-app memory so far?"', ''],
          ['         netti already drafted its pages from the readme and the file names.', 'dim'],
          ['       "remember that the launch moved to march"', ''],
          ['         keeps a decision. the next chat, in any tool, already knows.', 'dim'],
          ['       "remember i like short answers, code first"', ''],
          ['         keeps something about you, not about a project.', 'dim'],
          ['       "leave a handoff for tomorrow"', ''],
          ['         ends a session well. the next one, anywhere, starts from it.', 'dim'],
          [''],
          ['  3  bring what your tools already know about you:', ''],
          ['       npx innernet import claude          found 4 Claude Code memory files on this machine', 'p'],
          [''],
          ['  go further', ''],
          ['  · what to say, with examples      innernet.live/community/guides/what-to-say-to-your-ai', 'dim'],
          ['  · the guides, in five chapters    innernet.live/community/guides', 'dim'],
          ['  · this tour, in the terminal      npx innernet guide', 'dim'],
          ["  · what's connected, any time      npx innernet status", 'dim'],
        ]}
      />
    </Stage>
  ),

  // `innernet status` in the same repo, a day later (apps/cli/src/commands/account.ts)
  'terminal-status': () => (
    <Stage kind="term" title="lisbon-app — zsh">
      <Term
        lines={[
          ['~/code/lisbon-app $ npx innernet status', 'p'],
          [''],
          ['  innernet  what is connected here', ''],
          ['  ────────────────────────────────────────────────────', 'dim'],
          ['  ✓ signed in as sam@example.com · key in ~/.innernet/session.json', 'ok'],
          [''],
          ['  your ai tools  → https://innernet.live/api/mcp', ''],
          ['  ✓ Claude Code        connected', 'ok'],
          ['  ✓ Cursor             connected', 'ok'],
          ['  · Windsurf           found, not connected yet', 'dim'],
          ['    npx innernet connects the rest, and says why for any it cannot.', 'dim'],
          [''],
          ['  this folder  ~/code/lisbon-app', ''],
          ['  ✓ memory lisbon-app', 'ok'],
          ['  ✓ commits and pulls here are remembered', 'ok'],
          ['  ✓ last commit reached the memory 2 min ago: switch deposits to stripe', 'ok'],
          ['  ✓ AGENTS.md tells coding agents where the memory is', 'ok'],
        ]}
      />
    </Stage>
  ),

  connections: () => (
    <>
      <Stage url="innernet.live · connections">
        <div className="gf-head"><Crumb trail={['yours', 'connections']} menu={null} /><You /></div>
        <Ticker words={SPACES} on="connections" />
        <div className="gf-chips" style={{ margin: '0.3rem 0 0.9rem' }}>
          <Chip on>mcp</Chip><Chip>npx</Chip><Chip>api</Chip><Chip>extensions</Chip>
        </div>
        <p className="gf-col-label">the ones you talk to</p>
        <div className="gf-rows">
          <Row name={<span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}><BrandIcon name="Claude" size={15} />Claude Desktop</span>} meta={<><span className="gf-dot" data-live aria-hidden />live</>} />
          <Row name={<span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}><BrandIcon name="ChatGPT" size={15} />ChatGPT</span>} meta={<Hot n={1}><span className="gf-link">connect →</span></Hot>} />
        </div>
        <p className="gf-col-label" style={{ marginTop: '0.8rem' }}>frontier editors</p>
        <div className="gf-rows">
          <Row name={<span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}><BrandIcon name="Cursor" size={15} />Cursor · my laptop</span>} meta="waiting for its first call" />
          <Row name={<span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}><BrandIcon name="Claude Code" size={15} />Claude Code · work</span>} meta={<><span className="gf-dot" data-live aria-hidden />active</>} />
        </div>
      </Stage>
    </>
  ),

  'connect-window': () => (
    <Stage kind="plain">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}>
        <Chip>name</Chip><Chip on>connect</Chip><Chip>permissions</Chip><Chip>instructions</Chip><Chip>import</Chip>
      </div>
      <ol className="gf-legend" style={{ margin: 0, color: 'var(--gf-ink-2)' }}>
        <li><span className="gf-n">1</span><span>in Claude, open Customize → Connectors → Add</span></li>
        <li><span className="gf-n">2</span><span>name it innernet, paste the link, press Connect</span></li>
        <li><span className="gf-n">3</span><span>sign in to innernet in the window that opens, and allow</span></li>
      </ol>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', margin: '1rem 0 0.3rem', flexWrap: 'wrap' }}>
        <span className="gf-field" style={{ flex: 1, minWidth: '14rem', color: 'var(--gf-ink)', fontFamily: 'var(--font-mono)', fontSize: '0.76rem' }}>https://innernet.live/api/mcp</span>
        <Hot n={4}><Pill>i&apos;ve done this</Pill></Hot>
      </div>
    </Stage>
  ),

  'chat-load': () => (
    <Stage kind="chat" title="your ai">
      <div className="gf-chat">
        <span className="gf-say">load my projects from innernet</span>
        <Call tool="innernet_list_projects" />
        <span className="gf-reply">you have three: <b>lisbon-app</b> (code, updated an hour ago), <b>trip to lisbon</b> and <b>reading list</b>. want me to open one?</span>
      </div>
    </Stage>
  ),

  'chat-save': () => (
    <Stage kind="chat" title="your ai">
      <div className="gf-chat">
        <span className="gf-say">save this conversation to a new innernet project called &quot;trip to lisbon&quot;</span>
        <Call tool="innernet_save_context" />
        <span className="gf-reply">saved. <b>trip to lisbon</b> has four pages now: plan, bookings, people and open questions. it&apos;s on your dashboard.</span>
      </div>
    </Stage>
  ),

  'chat-remember': () => (
    <Stage kind="chat" title="your ai">
      <div className="gf-chat">
        <span className="gf-say">remember that the refund window is 14 days</span>
        <Call tool="innernet_capture" />
        <span className="gf-reply">kept, in <b>lisbon-app</b>. netti will fold it into the <b>pricing</b> page, and it&apos;s already there for the next question.</span>
        <span className="gf-say">and remember i like short answers, code first</span>
        <Call tool="innernet_self_capture" />
        <span className="gf-reply">noted, about you rather than the project. every tool you connect will know.</span>
      </div>
    </Stage>
  ),

  'chat-branch': () => (
    <Stage kind="chat" title="your ai">
      <div className="gf-chat">
        <span className="gf-say">branch lisbon-app as &quot;usage-pricing&quot; and rewrite the pricing page there as usage-based</span>
        <Call tool="innernet_branch_new" />
        <Call tool="innernet_save_context" />
        <span className="gf-reply">done, on <b>usage-pricing</b> only: a small monthly base, then per booking. trunk still says a flat seat. want to see the difference?</span>
        <span className="gf-say">yes, and merge it if nothing clashes</span>
        <Call tool="innernet_branch_diff" />
        <span className="gf-reply"><b>pricing</b> modified, <b>forecast</b> added. <b>positioning</b> changed on trunk too since you branched, so i left it as trunk has it. shall i merge the other two?</span>
      </div>
    </Stage>
  ),

  'chat-ask': () => (
    <Stage kind="chat" title="your ai">
      <div className="gf-chat">
        <span className="gf-say">what did we decide about the hotel, and why?</span>
        <Call tool="innernet_context" />
        <span className="gf-reply">the Alfama place, booked on the 4th. you dropped the one by the river because it had no workspace, which the <b>plan</b> page notes under its history.</span>
      </div>
    </Stage>
  ),

  'dashboard-tour': () => (
    <>
      <Stage url="innernet.live">
        <div className="gf-head">
          <Crumb trail={['yours', 'projects']} menu={1} />
          <Hot n={5}><You /></Hot>
        </div>
        <Hot n={2} box><Ticker words={SPACES} on="projects" /></Hot>
        <p className="gf-sum">everything you&apos;re building, one folder each. netti keeps the line under each one fresh.</p>
        <div className="gf-chips">
          <Hot n={3}><span style={{ display: 'inline-flex', gap: '0.4rem' }}><Chip on>grid</Chip><Chip>list</Chip><Chip>orbit</Chip><Chip>story</Chip></span></Hot>
          <Chip add>+ new view</Chip>
        </div>
        <div className="gf-split" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginTop: '1rem' }}>
          {[['lisbon-app', '214 cells'], ['trip to lisbon', '38 cells'], ['reading list', '12 cells']].map(([n, m]) => (
            <div key={n} style={{ padding: '0.8rem', borderRadius: 10, background: 'var(--gf-well)' }}>
              <div style={{ color: 'var(--gf-ink)' }}>{n}</div>
              <div className="gf-row__meta">{m}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '1.1rem' }}><Hot n={4}><span className="gf-dial__orb" style={{ position: 'static', display: 'inline-block' }} /></Hot></div>
      </Stage>
      <Legend items={[
        'the crumb says where you are. ≡ opens the whole map of innernet.',
        'your spaces. tap a word, or swipe the row, to move between them.',
        'the view chips. each is a way of looking at this place.',
        'the ground dial. the colour your innernet stands on.',
        'you. your name and your face, which opens the you space.',
      ]} />
    </>
  ),

  'site-menu': () => (
    <Stage kind="plain">
      <div className="gf-crumb" style={{ marginBottom: '0.8rem' }}><b>innernet</b><span>›</span><b>yours</b></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '1.3rem', letterSpacing: '-0.02em' }}>
        {['projects', 'you', 'netti', 'skills', 'connections', 'activity', 'tasks', 'parked', 'settings'].map((w) => (
          <span key={w} style={{ color: w === 'projects' ? 'var(--gf-ink)' : 'var(--gf-ink-3)' }}>
            {w === 'parked' ? <Hot n={1}><span>{w}</span></Hot> : w}
          </span>
        ))}
      </div>
      <p className="gf-row__meta" style={{ marginTop: '0.9rem' }}>homepage · company · docs · community · status</p>
    </Stage>
  ),

  'chips-reorder': () => (
    <>
      <Stage kind="plain">
        <div className="gf-chips" style={{ padding: '0.6rem 0 0.2rem' }}>
          <Chip ghost>list</Chip>
          <Chip on>grid</Chip>
          <Hot n={1}><Chip lift>list</Chip></Hot>
          <Chip>orbit</Chip>
          <Chip>story</Chip>
        </div>
        <p className="gf-row__meta" style={{ marginTop: '0.8rem' }}>let go, and list becomes the first view here, on every device.</p>
      </Stage>
      <Legend items={[
        'press and hold a chip for about half a second, until it lifts.',
        'drag it along the row and let go where you want it.',
        'the leftmost chip is the view this place opens in, from now on.',
      ]} />
    </>
  ),

  'ground-dial': () => (
    <Stage kind="plain">
      <div style={{ display: 'flex', gap: '1.4rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="gf-dial" aria-hidden>
          <svg viewBox="0 0 170 150">
            {[
              { r: 44, hue: 'hue', w: 7 },
              { r: 62, hue: 'sat', w: 7 },
              { r: 80, hue: 'light', w: 7 },
              { r: 98, hue: 'grad', w: 7 },
            ].map((a, i) => {
              const cx = 23
              const cy = 127
              const x2 = cx + a.r
              const y1 = cy - a.r
              return (
                <path
                  key={a.hue}
                  d={`M ${cx} ${y1} A ${a.r} ${a.r} 0 0 1 ${x2} ${cy}`}
                  fill="none"
                  strokeWidth={a.w}
                  strokeLinecap="round"
                  stroke={i === 0 ? 'url(#gf-hue)' : `hsl(var(--organism-hue) ${40 + i * 12}% ${70 - i * 8}% / ${0.9 - i * 0.12})`}
                />
              )
            })}
            <defs>
              <linearGradient id="gf-hue" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="hsl(20 80% 70%)" />
                <stop offset="0.33" stopColor="hsl(110 60% 65%)" />
                <stop offset="0.66" stopColor="hsl(210 70% 68%)" />
                <stop offset="1" stopColor="hsl(300 60% 70%)" />
              </linearGradient>
            </defs>
          </svg>
          <span style={{ position: 'absolute', left: 10, bottom: 10 }}>
            <Hot n={1}><span className="gf-dial__orb" style={{ position: 'static', display: 'block' }} /></Hot>
          </span>
        </div>
        <div style={{ flex: 1, minWidth: '12rem', color: 'var(--gf-ink-2)' }}>
          <p style={{ margin: 0 }}>the four arcs, inside out: <b>hue</b>, <b>saturation</b>, <b>brightness</b>, and how much <b>gradient</b> the ground carries.</p>
          <p style={{ margin: '0.5rem 0 0' }}>the page repaints as you drag, and saves when you let go.</p>
        </div>
      </div>
    </Stage>
  ),

  'project-open': () => (
    <Stage url="innernet.live · lisbon-app">
      <div className="gf-head"><Crumb trail={['yours', 'projects', 'lisbon-app']} menu={null} /><You /></div>
      <div className="gf-ticker" aria-hidden><span data-on>lisbon-app</span><span>trip to lisbon</span><span>reading list</span></div>
      <p className="gf-sum">a booking app for small guesthouses in lisbon. next.js on vercel, stripe for deposits.</p>
      <div className="gf-chips" style={{ marginBottom: '0.8rem' }}>
        <Chip on>grid</Chip><Chip>list</Chip><Chip>artifacts</Chip><Hot n={2}><Chip>tasks</Chip></Hot><Hot n={3}><Chip>timeline</Chip></Hot><Chip>repo</Chip>
      </div>
      <div className="gf-rows">
        <Row name={<Hot n={1}><span>vision</span></Hot>} meta="domain" />
        <Row name="technical" meta="domain" />
        <Row name="pricing" meta="domain" />
        <Row name="inbox" meta="3 resting" />
        <Row name="handoffs" meta="latest · yesterday" />
      </div>
    </Stage>
  ),

  'page-edit': () => (
    <Stage kind="plain">
      <div style={{ borderRadius: 10, boxShadow: '0 0 0 2px var(--gf-acc)', padding: '0.9rem 1rem' }}>
        <div className="gf-head" style={{ marginBottom: '0.5rem' }}>
          <span className="gf-row__meta">editing memory</span>
          <span style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
            <span className="gf-link">cancel</span>
            <Hot n={2}><Pill>save</Pill></Hot>
          </span>
        </div>
        <div className="gf-doc">
          <h4>pricing</h4>
          <p>usage-based: a small monthly base, then per booking. <mark>deposits go through stripe, held until check-in.</mark></p>
        </div>
      </div>
      <Legend items={['press the pencil, or double-click the words, to start editing.', 'save when it says what you mean. esc cancels.']} />
    </Stage>
  ),

  history: () => (
    <Stage kind="plain">
      <div className="gf-doc">
        <h4>pricing</h4>
        <p>usage-based: a small monthly base, then per booking. deposits go through stripe, held until check-in.</p>
        <div className="gf-hist">
          <div>## history</div>
          <div>- 2026-09-12 · pricing: was a flat monthly seat → now usage-based</div>
          <div>- 2026-08-30 · deposits: was bank transfer → now stripe, held until check-in</div>
        </div>
      </div>
    </Stage>
  ),

  timeline: () => (
    <Stage url="innernet.live · lisbon-app · timeline">
      <div className="gf-chips" style={{ marginBottom: '0.8rem' }}><Chip>grid</Chip><Chip>tasks</Chip><Chip on>timeline</Chip><Chip>repo</Chip></div>
      <div className="gf-rows">
        <Row name="fold: pricing moved to usage-based" meta="fold · today" dot="live" />
        <Row name="add stripe webhook for held deposits" meta="git · today" dot="quiet" />
        <Row name="save: the hotel shortlist" meta="mcp · yesterday" dot="quiet" />
        <Row name="genesis: 6 pages from the readme" meta="genesis · sep 2" dot="quiet" />
      </div>
    </Stage>
  ),

  tasks: () => (
    <Stage kind="plain">
      <div className="gf-rows">
        <Row name={<><span className="gf-dot" aria-hidden />wire the stripe webhook</>} meta="in progress · high" />
        <Row name={<><span className="gf-dot" aria-hidden />write the guesthouse onboarding email</>} meta="not started · medium" />
        <Row name={<span style={{ textDecoration: 'line-through' }}>pick a hosting region</span>} meta="done" dim />
      </div>
      <div style={{ marginTop: '0.8rem' }}><Hot n={1} box><span className="gf-field" style={{ flex: 1 }}>add a task…</span></Hot></div>
    </Stage>
  ),

  branch: () => (
    <>
      <Stage kind="plain">
        <div className="gf-split">
          <div>
            <p className="gf-col-label">trunk · what everyone reads</p>
            <div className="gf-doc"><h4>pricing</h4><p>a flat monthly seat per guesthouse.</p></div>
          </div>
          <div>
            <p className="gf-col-label">branch · usage-pricing</p>
            <div className="gf-doc"><h4>pricing</h4><p><mark>a small base, then per booking.</mark></p></div>
          </div>
        </div>
        <div className="gf-rows" style={{ marginTop: '1rem' }}>
          <Row name="pricing" meta={<span className="gf-tag">modified</span>} />
          <Row name="forecast" meta={<span className="gf-tag">added</span>} />
          <Row name="positioning" meta={<span className="gf-tag" data-private>conflict · trunk changed it too</span>} />
        </div>
      </Stage>
      <Legend items={[
        'fork: "branch lisbon-app as usage-pricing". trunk is untouched.',
        'diff: what the branch changed since the fork, and what trunk changed too.',
        'merge: only the branch’s changes land. a conflict stays as trunk has it until you settle it.',
      ]} />
    </>
  ),

  'you-private': () => (
    <Stage url="innernet.live · you · private">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}><Chip>summary</Chip><Chip>story</Chip><Chip>mirror</Chip><Chip on>private</Chip></div>
      <p style={{ margin: '0 0 0.6rem', fontSize: '1.05rem' }}>what innernet knows about you, and who gets to see it</p>
      <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', margin: '0.4rem 0 0.4rem' }} aria-hidden>
        {[[9, 18], [6, 30], [30, 48], [34, 62], [21, 78]].map(([w, l], i) => (
          <span key={i} style={{ width: `${w}%`, background: `hsl(var(--organism-hue) 50% ${l}%)` }} />
        ))}
      </div>
      <p className="gf-row__meta" style={{ margin: '0 0 0.8rem' }}>private · self · trusted · work · public &nbsp;·&nbsp; 11 things never leave innernet.</p>
      <div className="gf-rows">
        <Row name={<Hot n={1}><span>prefers short answers, then the detail on ask</span></Hot>} meta={<span className="gf-tag">trusted</span>} />
        <Row name="works in TypeScript and Next.js" meta={<span className="gf-tag">work</span>} />
        <Row name="sleeps badly before a launch" meta={<span className="gf-tag" data-private>private</span>} />
      </div>
    </Stage>
  ),

  'fact-source': () => (
    <>
      <Stage kind="plain">
        <p className="gf-col-label">where it came from</p>
        <p style={{ margin: '0 0 0.2rem', color: 'var(--gf-ink-2)' }}>a line you said, in Claude, on sep 21</p>
        <p style={{ margin: '0 0 1rem', paddingLeft: '0.8rem', boxShadow: 'inset 2px 0 0 var(--gf-acc)', color: 'var(--gf-ink)' }}>&ldquo;just give me the short version first, i&apos;ll ask if i want more.&rdquo;</p>
        <p className="gf-col-label">who can see it</p>
        <div className="gf-chips" style={{ marginBottom: '1rem' }}>
          <Chip>private</Chip><Chip>self</Chip><Hot n={1}><Chip on>trusted</Chip></Hot><Chip>work</Chip><Chip>public</Chip>
        </div>
        <span style={{ display: 'flex', gap: '1.1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Hot n={2}><span className="gf-link">edit</span></Hot><span className="gf-link">share</span><Hot n={3}><span className="gf-link">forget</span></Hot>
        </span>
      </Stage>
      <Legend items={[
        'who can see it: move a fact to private and no ai tool, room or link ever sees it again.',
        'edit: change the words and save this version. the old words stay, locked, as its history.',
        'forget: asks once more, then it is gone for good. every tool stops seeing it.',
      ]} />
    </>
  ),

  summary: () => (
    <Stage url="innernet.live · you · summary">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}><Chip on>summary</Chip><Chip>story</Chip><Chip>mirror</Chip><Chip>private</Chip></div>
      <p style={{ margin: 0, fontSize: '1.05rem' }}>a day that went mostly to the booking flow, and one decision you&apos;d been putting off.</p>
      <div className="gf-rows" style={{ marginTop: '0.8rem' }}>
        <Row name="threads you pulled" meta="3" />
        <Row name="decided" meta="usage-based pricing" />
        <Row name="left open" meta="the refund policy" />
        <Row name="a question for tomorrow" meta="who writes the host email?" />
      </div>
    </Stage>
  ),

  inbox: () => (
    <Stage url="innernet.live · activity · inbox">
      <div className="gf-chips" style={{ marginBottom: '0.8rem' }}><Chip>river</Chip><Chip>repos</Chip><Chip on>inbox</Chip></div>
      <p className="gf-col-label">today</p>
      <div className="gf-rows">
        <Row name="prefers short answers, then the detail on ask" sub="new fact" meta={<Hot n={1}><span className="gf-link">see it where it lives</span></Hot>} />
        <Row name="deposits: bank transfer → stripe" sub="changed" meta="lisbon-app" />
        <Row name={<span style={{ filter: 'blur(4px)' }}>something private you said</span>} sub="a line you said · private" meta={<Hot n={2}><span className="gf-link">show it</span></Hot>} />
        <Row name="you decide fastest after a walk" sub="a pattern" meta={<span style={{ display: 'inline-flex', gap: '0.7rem' }}><Hot n={3}><span className="gf-link">resonates</span></Hot><span className="gf-link">not quite</span></span>} />
      </div>
    </Stage>
  ),

  cells: () => (
    <Stage url="innernet.live · netti · you & netti">
      <div style={{ display: 'flex', gap: '1.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Netti size={74} />
        <div className="gf-nums" style={{ flex: 1, margin: 0, minWidth: '16rem' }}>
          <div className="gf-num"><b>264</b><span>cells</span></div>
          <div className="gf-num"><b>3</b><span>spaces</span></div>
          <div className="gf-num"><b>41</b><span>personal</span></div>
          <div className="gf-num"><b>38%</b><span>grown</span></div>
        </div>
      </div>
    </Stage>
  ),

  repo: () => (
    <Stage url="innernet.live · lisbon-app · repo">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}><Chip>grid</Chip><Chip>timeline</Chip><Chip on>repo</Chip></div>
      <svg viewBox="0 0 520 150" width="100%" aria-hidden style={{ display: 'block' }}>
        <text x="0" y="14" fill="currentColor" opacity="0.45" fontSize="11">today</text>
        <text x="0" y="96" fill="currentColor" opacity="0.45" fontSize="11">yesterday</text>
        <path d="M80 30 V140" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2" />
        <path d="M80 110 C 80 90, 110 90, 110 70 V 34" stroke="hsl(var(--organism-hue) 70% 46%)" strokeWidth="2" fill="none" />
        {[30, 58, 124].map((y) => <circle key={y} cx="80" cy={y} r="5" fill="var(--gf-sheet)" stroke="currentColor" strokeOpacity="0.55" strokeWidth="2" />)}
        {[40, 70].map((y) => <circle key={y} cx="110" cy={y} r="5" fill="var(--gf-sheet)" stroke="hsl(var(--organism-hue) 70% 46%)" strokeWidth="2" />)}
        <text x="130" y="34" fill="currentColor" fontSize="12">main · merge #42 held deposits</text>
        <text x="130" y="62" fill="currentColor" opacity="0.8" fontSize="12">main · fix date picker in safari</text>
        <text x="130" y="44" fill="hsl(var(--organism-hue) 60% 38%)" fontSize="12" dy="30">stripe-deposits · add webhook</text>
        <text x="130" y="128" fill="currentColor" opacity="0.8" fontSize="12">main · first booking flow</text>
      </svg>
      <p className="gf-row__meta" style={{ marginTop: '0.4rem' }}>hover a line to light its branch. click it for its commits and its pull request.</p>
    </Stage>
  ),

  'chat-window': () => (
    <>
      <Stage kind="chat" title="your ai">
        <div className="gf-chat">
          <span className="gf-reply">the webhook goes in app/api/stripe. i&apos;ve kept the decision about held deposits.</span>
        </div>
        <div style={{ marginTop: '0.8rem' }}>
          <Hot n={1} box>
            <span className="gf-strip" style={{ flex: 1 }}>
              <BrandIcon name="innernet" size={16} />
              <span className="gf-strip__line">pricing: usage-based since the 12th, kept</span>
              <span className="gf-strip__n">read 4 · saved 1</span>
              <Chevron />
            </span>
          </Hot>
        </div>
        <div style={{ marginTop: '0.9rem', padding: '0.9rem', borderRadius: 12, background: 'var(--gf-well)' }}>
          <p className="gf-col-label" style={{ margin: 0 }}>lisbon-app · the booking flow</p>
          <p className="gf-row__meta" style={{ margin: '0.2rem 0 0' }}>main · 3f9a2c1 · add stripe webhook for held deposits</p>
          <div className="gf-nums">
            <div className="gf-num"><b>4</b><span>read</span></div>
            <div className="gf-num"><b>1</b><span>saved</span></div>
            <div className="gf-num"><b>1.4k</b><span>sent</span></div>
            <div className="gf-num"><b>2</b><span>held back</span></div>
          </div>
          <div className="gf-rows">
            <Row name={<Hot n={2}><span>pricing · usage-based</span></Hot>} meta="opens in innernet" />
            <Row name="technical · stripe deposits" meta="opens in innernet" />
          </div>
          <div className="gf-foot"><span>open in innernet</span><span>let it rest in the chat</span></div>
        </div>
      </Stage>
      <Legend items={[
        'the strip: one line of what innernet just found or kept. tap it to open.',
        'the ledger: every row is a door into that exact page of your dashboard.',
      ]} />
    </>
  ),

  'chat-settings': () => (
    <Stage url="innernet.live · settings · in your chats">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}><Chip>account</Chip><Chip>app</Chip><Chip on>in your chats</Chip><Chip>security</Chip><Chip>data</Chip></div>
      <p className="gf-col-label">where innernet shows up</p>
      <div className="gf-rows">
        <Row name="the window" meta={<Hot n={1}><span className="gf-tag">on</span></Hot>} />
        <Row name="the signature line" meta={<span className="gf-tag">on</span>} />
      </div>
      <p className="gf-col-label" style={{ marginTop: '0.8rem' }}>the window</p>
      <div className="gf-rows">
        <Row name="when it arrives" meta={<span style={{ display: 'inline-flex', gap: '0.3rem' }}><Chip on>one line</Chip><Chip>open</Chip></span>} />
        <Row name="the line rolls" meta={<span className="gf-tag">on</span>} />
        <Row name="a row opens" meta={<Hot n={2}><span style={{ display: 'inline-flex', gap: '0.3rem' }}><Chip on>innernet, in a tab</Chip><Chip>in the window</Chip></span></Hot>} />
      </div>
    </Stage>
  ),

  'share-studio': () => (
    <>
      <Stage url="innernet.live · share">
        <div className="gf-split">
          <div style={{ padding: '0.8rem', borderRadius: 10, background: 'hsl(var(--organism-hue) 40% 90%)' }}>
            <div className="gf-doc"><h4>trip to lisbon · plan</h4><p>four days, alfama, one day trip to sintra…</p></div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
            <p className="gf-col-label" style={{ margin: 0 }}>who it opens for · takes effect at once</p>
            <span className="gf-chips"><Hot n={1}><Chip on>anyone with the link</Chip></Hot><Chip>only you</Chip></span>
            <span className="gf-field" style={{ color: 'var(--gf-ink)', fontSize: '0.76rem' }}>innernet.live/share/k2x9…</span>
            <span style={{ display: 'flex', gap: '0.9rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Hot n={3}><Pill>post it live</Pill></Hot>
              <span className="gf-link">let this link go</span>
            </span>
          </div>
        </div>
        <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <Hot n={2}><span className="gf-dial__orb" style={{ position: 'static', display: 'inline-block', ['--gf-swatch' as string]: 'hsl(var(--organism-hue) 40% 90%)' } as React.CSSProperties} /></Hot>
          <span className="gf-row__meta">the ground this link opens in</span>
        </div>
      </Stage>
      <Legend items={[
        'who it opens for. anyone with the link, or only you. it changes at once.',
        'the ground the link opens in. follow yours live, or fix one for this link.',
        'post it live. the same button updates it later.',
      ]} />
    </>
  ),

  'skill-link': () => (
    <Stage kind="plain">
      <p style={{ margin: '0 0 0.6rem', color: 'var(--gf-ink-2)' }}>paste this link into your ai. it will know what to do.</p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <span className="gf-field" style={{ flex: 1, background: '#fff', color: 'var(--gf-ink)', boxShadow: '0 1px 10px 5px rgb(255 255 255 / .27), inset 0 0 0 1px var(--gf-hair)', borderRadius: 999 }}>innernet.live/community/skills/sam/trip-planner</span>
        <Hot n={1}><span className="gf-pill" style={{ padding: 0 }}><i style={{ width: '2rem', height: '2rem' }}>⧉</i></span></Hot>
      </div>
    </Stage>
  ),

  forget: () => (
    <Stage kind="plain">
      <p style={{ margin: '0 0 0.3rem', fontSize: '1.05rem' }}>forget &ldquo;old-pricing&rdquo;?</p>
      <p style={{ margin: '0 0 0.9rem', color: 'var(--gf-ink-2)' }}>a page in lisbon-app, with its 6 earlier versions. every tool stops seeing it, and there is no undo.</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
        <span className="gf-link">keep it</span>
        <Hot n={1}><Pill>yes, forget it</Pill></Hot>
      </div>
    </Stage>
  ),

  'api-keys': () => (
    <Stage url="innernet.live · connections · api">
      <div className="gf-chips" style={{ marginBottom: '0.9rem' }}><Chip>mcp</Chip><Chip>npx</Chip><Chip on>api</Chip><Chip>extensions</Chip></div>
      <p className="gf-col-label">your keys</p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="gf-field" style={{ flex: 1, minWidth: '10rem' }}>key name (e.g. staging)</span>
        <Chip on>read</Chip><Chip>write</Chip>
        <Hot n={1}><Pill>create key</Pill></Hot>
      </div>
      <div className="gf-rows" style={{ marginTop: '0.8rem' }}>
        <Row name="ci · deploys" meta="read, write · used today" />
      </div>
    </Stage>
  ),

  // ── agents (2026-10-19): the agents you keep, connecting one, its memory, its access, its river ──
  'agents-kept': () => (
    <Stage url="innernet.live · connections">
      <div className="gf-head"><Crumb trail={['yours', 'connections']} menu={null} /><You /></div>
      <Ticker words={SPACES} on="connections" />
      <div className="gf-chips" style={{ margin: '0.3rem 0 0.9rem' }}>
        <Chip on>tools</Chip><Chip>extensions</Chip><Chip>api</Chip><Chip>vault</Chip>
      </div>
      <p className="gf-col-label">the agents you keep</p>
      <p className="gf-sum" style={{ marginTop: 0 }}>
        each agent keeps a memory of its own, sealed to you, sorted by what kind of thing it is.{' '}
        <Hot n={2}><span className="gf-link">walk me through it</span></Hot>
      </p>
      <div className="gf-rows">
        <Row name="support desk" meta="keeps 1,906 · 3 sub-agents" />
        <Row name="inbox keeper" meta="keeps 214" />
      </div>
      <div style={{ marginTop: '0.7rem' }}><Hot n={1}><span className="gf-link">make an agent of your own</span></Hot></div>
    </Stage>
  ),

  'agent-connect': () => (
    <Stage kind="plain">
      <div className="gf-split">
        <div>
          <p className="gf-col-label">connect an agent</p>
          <p className="gf-col-label" style={{ margin: '0.6rem 0 0.25rem' }}>name</p>
          <div className="gf-field" data-lit>inbox keeper</div>
          <p className="gf-col-label" style={{ margin: '0.6rem 0 0.25rem' }}>it works for</p>
          <div className="gf-chips"><Chip on>you</Chip><Chip>other people</Chip></div>
          <p className="gf-col-label" style={{ margin: '0.6rem 0 0.25rem' }}>what it does (if you like)</p>
          <div className="gf-field">sorts my email every morning</div>
          <div style={{ marginTop: '0.8rem' }}><Hot n={1}><Pill>connect it</Pill></Hot></div>
        </div>
        <div>
          <p className="gf-col-label">inbox keeper · how it connects</p>
          <div className="gf-chips" style={{ margin: '0.5rem 0 0.6rem' }}><Chip on>your ai</Chip><Chip>typescript</Chip><Chip>python</Chip></div>
          <p className="gf-sum" style={{ margin: '0 0 0.5rem', fontSize: '0.74rem' }}>paste this into the ai you code with. it reads the link, finds where your agent answers, and wires the memory in.</p>
          <div className="gf-field" data-lit style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', lineHeight: 1.55 }}>
            give my agent a memory with innernet. read innernet.live/skills/agent-memory/raw and follow it. my agent&apos;s key is innernet_••••…
          </div>
          <div style={{ marginTop: '0.6rem' }}><Hot n={2}><span className="gf-link">copy for your ai</span></Hot></div>
        </div>
      </div>
    </Stage>
  ),

  'agent-paste': () => (
    <Stage kind="chat" title="Claude Code · my-support-bot">
      <div className="gf-chat">
        <span className="gf-say">give my agent a memory with innernet. read innernet.live/skills/agent-memory/raw and follow it. my agent&apos;s key is innernet_••••…</span>
        <span className="gf-reply">read it. your bot answers in <b style={{ fontWeight: 400, color: 'var(--gf-ink)' }}>src/chat.ts</b>. i put the key in .env as INNERNET_AGENT_KEY, and added two calls: what it remembers goes into the prompt before it answers, and the conversation is kept after it replies, per customer.</span>
        <span className="gf-reply">tried it: told it &ldquo;i&apos;m in lisbon&rdquo;, then asked the time. it answered in lisbon time. open your agent in innernet to see what it kept.</span>
      </div>
    </Stage>
  ),

  'agent-memory': () => (
    <Stage url="innernet.live · agents · inbox keeper">
      <div className="gf-head"><Crumb trail={['agents', 'inbox keeper']} menu={null} /><You /></div>
      <Ticker words={['memory', 'access', 'activity', 'settings']} on="memory" />
      <div className="gf-chips" style={{ margin: '0.3rem 0 0.9rem' }}>
        <Chip>overview</Chip><Hot n={1}><Chip on>what it keeps 214</Chip></Hot><Chip>projects 1</Chip><Chip>sub-agents</Chip>
      </div>
      <p className="gf-col-label">how it works · 3</p>
      <div className="gf-rows">
        <Row name="for a newsletter, keep the sender and the one line worth reading" meta="procedure" />
      </div>
      <p className="gf-col-label" style={{ marginTop: '0.7rem' }}>what it knows · 41</p>
      <div className="gf-rows">
        <Row name="the team standup moved to 10:30 on mondays" meta="fact" />
      </div>
      <p className="gf-col-label" style={{ marginTop: '0.7rem' }}>what people like · 12</p>
      <div className="gf-rows">
        <Row name="the owner prefers replies after 10 am" meta={<>preference<span className="gf-tag">waiting for you</span></>} />
      </div>
    </Stage>
  ),

  'agent-access': () => (
    <Stage url="innernet.live · agents · inbox keeper · access">
      <Ticker words={['access', 'activity', 'settings', 'memory']} on="access" />
      <p className="gf-col-label" style={{ marginTop: '0.6rem' }}>its level</p>
      <p className="gf-sum" style={{ margin: '0 0 0.4rem' }}>it reads what any app may, and anything it learns about you waits for your yes.</p>
      <div className="gf-chips"><Chip>sealed</Chip><Hot n={1}><Chip on>asks first</Chip></Hot><Chip>trusted</Chip></div>
      <div className="gf-rows" style={{ marginTop: '0.9rem' }}>
        <Row name="reads of you" sub="inbox keeper reads what any connected app may, never what you keep private." meta="like any app" />
        <Row name="writes about you" sub="what it learns about you waits here for your yes." meta="asks first" />
        <Row name="takes in" sub="it pulls what matters out and sorts each piece by kind." meta="sorted" />
        <Row name="hands out" sub="its answers carry its own memory only." meta="its own memory" />
      </div>
    </Stage>
  ),

  'agent-river': () => (
    <Stage url="innernet.live · agents · inbox keeper · activity">
      <Ticker words={['activity', 'settings', 'memory', 'access']} on="activity" />
      <div className="gf-chips" style={{ margin: '0.3rem 0 0.9rem' }}><Chip on>river 3</Chip><Chip>figures</Chip><Chip>rituals</Chip></div>
      <p className="gf-col-label">today</p>
      <div className="gf-rows">
        <Row name="inbox keeper recalled 6 memories" meta="4m ago" />
        <Row name="prefers replies after 10 am" sub="inbox keeper · what you like · waiting for you" meta={<Hot n={1}><span className="gf-link">keep · let go</span></Hot>} />
        <Row name="inbox keeper kept 2" meta="38m ago" />
      </div>
    </Stage>
  ),

  handoff: () => (
    <Stage kind="chat" title="your ai · the next morning">
      <div className="gf-chat">
        <span className="gf-say">where did we leave off?</span>
        <Call tool="innernet_handoff_read" />
        <span className="gf-reply">yesterday you got the webhook working locally. left: test a refund, then ask Ana about the host email. want to start with the refund?</span>
      </div>
    </Stage>
  ),
}

/** Draw a figure by name (unknown names draw nothing), with its caption. */
export function renderGuideFigure(name: string, caption: string, key: string): React.ReactNode {
  const draw = FIGURES[name]
  if (!draw) return null
  return (
    <figure key={key} className="gf" data-figure={name}>
      {draw()}
      {caption && <figcaption className="gf__cap">{caption}</figcaption>}
    </figure>
  )
}

export const GUIDE_FIGURE_NAMES = Object.keys(FIGURES)
